import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Users, Play, Eye, EyeOff, RotateCcw, Trophy, Skull, ThumbsUp, ChevronRight, ChevronLeft, Vote, Target } from 'lucide-react';

const IMPOSTOR_IMAGE = "https://static.prod-images.emergentagent.com/jobs/66a28a00-9b1e-456c-a7b7-f65afd6c930c/images/7478a3a151a70a7e0a604240650fe1996a56b6be78dcc8ccd7547523ea46b063.png";

function Impostor() {
  const navigate = useNavigate();
  
  // Estados del juego
  const [gamePhase, setGamePhase] = useState('setup'); // setup, distribution, playing, voting, results
  const [numPlayers, setNumPlayers] = useState(4);
  const [numRounds, setNumRounds] = useState(3);
  const [gameMode, setGameMode] = useState('mk'); // mk, normal
  const [playerNames, setPlayerNames] = useState([]);
  
  // Estados de la partida
  const [currentRound, setCurrentRound] = useState(1);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [impostorIndex, setImpostorIndex] = useState(null);
  const [secretWord, setSecretWord] = useState('');
  const [showRole, setShowRole] = useState(false);
  const [usedWords, setUsedWords] = useState([]);
  
  // Estados de votación
  const [votes, setVotes] = useState({});
  const [currentVoterIndex, setCurrentVoterIndex] = useState(0);
  const [roundResults, setRoundResults] = useState([]);
  const [scores, setScores] = useState({});

  // Inicializar nombres de jugadores cuando cambia el número
  useEffect(() => {
    const names = [];
    for (let i = 0; i < numPlayers; i++) {
      names.push(`Jugador ${i + 1}`);
    }
    setPlayerNames(names);
  }, [numPlayers]);

  // Iniciar nueva partida
  const startGame = async () => {
    // Inicializar puntuaciones
    const initialScores = {};
    playerNames.forEach(name => {
      initialScores[name] = 0;
    });
    setScores(initialScores);
    setRoundResults([]);
    setCurrentRound(1);
    setUsedWords([]);
    
    await startNewRound();
  };

  // Iniciar nueva ronda
  const startNewRound = async () => {
    try {
      // Obtener palabra aleatoria
      const response = await axios.get(`${API}/impostor/palabra-aleatoria/${gameMode}`);
      const palabra = response.data.palabra;
      
      // Evitar palabras repetidas
      if (usedWords.includes(palabra)) {
        const altResponse = await axios.get(`${API}/impostor/palabra-aleatoria/${gameMode}`);
        setSecretWord(altResponse.data.palabra);
        setUsedWords([...usedWords, altResponse.data.palabra]);
      } else {
        setSecretWord(palabra);
        setUsedWords([...usedWords, palabra]);
      }
      
      // Elegir impostor aleatorio
      const impostor = Math.floor(Math.random() * numPlayers);
      setImpostorIndex(impostor);
      
      // Resetear estado
      setCurrentPlayerIndex(0);
      setShowRole(false);
      setVotes({});
      setCurrentVoterIndex(0);
      setGamePhase('distribution');
    } catch (error) {
      console.error('Error al iniciar ronda:', error);
    }
  };

  // Mostrar rol del jugador actual
  const revealRole = () => {
    setShowRole(true);
  };

  // Pasar al siguiente jugador en distribución
  const nextPlayerDistribution = () => {
    setShowRole(false);
    if (currentPlayerIndex < numPlayers - 1) {
      setCurrentPlayerIndex(currentPlayerIndex + 1);
    } else {
      // Todos han visto su rol, empezar fase de juego
      setGamePhase('playing');
    }
  };

  // Ir a votación
  const goToVoting = () => {
    setGamePhase('voting');
    setCurrentVoterIndex(0);
    setVotes({});
  };

  // Registrar voto
  const castVote = (votedPlayerIndex) => {
    const voterName = playerNames[currentVoterIndex];
    const votedName = playerNames[votedPlayerIndex];
    
    const newVotes = { ...votes, [voterName]: votedName };
    setVotes(newVotes);
    
    if (currentVoterIndex < numPlayers - 1) {
      setCurrentVoterIndex(currentVoterIndex + 1);
    } else {
      // Todos han votado, calcular resultados
      calculateResults(newVotes);
    }
  };

  // Calcular resultados de la ronda
  const calculateResults = (finalVotes) => {
    // Contar votos
    const voteCounts = {};
    playerNames.forEach(name => {
      voteCounts[name] = 0;
    });
    
    Object.values(finalVotes).forEach(votedName => {
      voteCounts[votedName]++;
    });
    
    // Encontrar el más votado
    let maxVotes = 0;
    let mostVoted = null;
    Object.entries(voteCounts).forEach(([name, count]) => {
      if (count > maxVotes) {
        maxVotes = count;
        mostVoted = name;
      }
    });
    
    const impostorName = playerNames[impostorIndex];
    const impostorCaught = mostVoted === impostorName;
    
    // Actualizar puntuaciones
    const newScores = { ...scores };
    if (impostorCaught) {
      // Todos excepto el impostor ganan 1 punto
      playerNames.forEach((name, idx) => {
        if (idx !== impostorIndex) {
          newScores[name] += 1;
        }
      });
    } else {
      // El impostor gana 2 puntos
      newScores[impostorName] += 2;
    }
    setScores(newScores);
    
    // Guardar resultado de la ronda
    const roundResult = {
      round: currentRound,
      word: secretWord,
      impostor: impostorName,
      mostVoted: mostVoted,
      caught: impostorCaught,
      votes: voteCounts
    };
    setRoundResults([...roundResults, roundResult]);
    
    setGamePhase('results');
  };

  // Siguiente ronda o fin del juego
  const nextRoundOrEnd = async () => {
    if (currentRound < numRounds) {
      setCurrentRound(currentRound + 1);
      await startNewRound();
    } else {
      setGamePhase('final');
    }
  };

  // Reiniciar juego
  const resetGame = () => {
    setGamePhase('setup');
    setCurrentRound(1);
    setCurrentPlayerIndex(0);
    setImpostorIndex(null);
    setSecretWord('');
    setShowRole(false);
    setVotes({});
    setCurrentVoterIndex(0);
    setRoundResults([]);
    setScores({});
    setUsedWords([]);
  };

  // Renderizar pantalla de configuración
  const renderSetup = () => (
    <div className="max-w-md mx-auto space-y-6">
      <Card className="card-mk">
        <CardHeader>
          <CardTitle className="text-gold text-center text-2xl">Configuración</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Número de jugadores */}
          <div className="space-y-2">
            <Label className="text-gray-300">Número de jugadores</Label>
            <Select value={numPlayers.toString()} onValueChange={(v) => setNumPlayers(parseInt(v))}>
              <SelectTrigger className="bg-gray-800 border-gray-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                {[3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <SelectItem key={n} value={n.toString()}>{n} jugadores</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Modo de juego */}
          <div className="space-y-2">
            <Label className="text-gray-300">Modo de juego</Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant={gameMode === 'mk' ? 'default' : 'outline'}
                className={gameMode === 'mk' ? 'bg-gold text-black' : 'border-gray-600'}
                onClick={() => setGameMode('mk')}
              >
                💪 Modo MK
              </Button>
              <Button
                type="button"
                variant={gameMode === 'normal' ? 'default' : 'outline'}
                className={gameMode === 'normal' ? 'bg-gold text-black' : 'border-gray-600'}
                onClick={() => setGameMode('normal')}
              >
                🌍 Normal
              </Button>
            </div>
            <p className="text-xs text-gray-500 text-center">
              {gameMode === 'mk' ? 'Palabras de fitness y crossfit' : 'Cultura general'}
            </p>
          </div>

          {/* Número de rondas */}
          <div className="space-y-2">
            <Label className="text-gray-300">Número de rondas</Label>
            <Select value={numRounds.toString()} onValueChange={(v) => setNumRounds(parseInt(v))}>
              <SelectTrigger className="bg-gray-800 border-gray-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? 'ronda' : 'rondas'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Nombres de jugadores */}
          <div className="space-y-2">
            <Label className="text-gray-300">Nombres de jugadores</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {playerNames.map((name, index) => (
                <Input
                  key={index}
                  value={name}
                  onChange={(e) => {
                    const newNames = [...playerNames];
                    newNames[index] = e.target.value;
                    setPlayerNames(newNames);
                  }}
                  placeholder={`Jugador ${index + 1}`}
                  className="bg-gray-800 border-gray-700"
                />
              ))}
            </div>
          </div>

          <Button 
            className="w-full btn-primary-mk text-lg py-6"
            onClick={startGame}
          >
            <Play className="mr-2" /> ¡Comenzar Partida!
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // Renderizar pantalla de distribución de roles
  const renderDistribution = () => (
    <div className="max-w-md mx-auto text-center space-y-6">
      {!showRole ? (
        // Pantalla de "pasa el móvil"
        <Card className="card-mk overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-gradient-to-b from-yellow-600 to-yellow-700 p-6">
              <img 
                src={IMPOSTOR_IMAGE} 
                alt="Pasa el móvil" 
                className="w-48 h-48 mx-auto rounded-full border-4 border-white shadow-lg"
              />
            </div>
            <div className="p-6 space-y-4">
              <h2 className="text-2xl font-bold text-gold">
                📱 Pasa el móvil a...
              </h2>
              <p className="text-4xl font-bold text-white">
                {playerNames[currentPlayerIndex]}
              </p>
              <p className="text-gray-400">
                Jugador {currentPlayerIndex + 1} de {numPlayers}
              </p>
              <Button 
                className="w-full btn-primary-mk text-lg py-6 mt-4"
                onClick={revealRole}
              >
                <Eye className="mr-2" /> Ver mi rol
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Pantalla de rol
        <Card className={`card-mk ${currentPlayerIndex === impostorIndex ? 'border-red-500 border-2' : 'border-green-500 border-2'}`}>
          <CardContent className="p-8 space-y-6">
            {currentPlayerIndex === impostorIndex ? (
              // Es el impostor
              <>
                <div className="text-6xl">🎭</div>
                <h2 className="text-3xl font-bold text-red-500">
                  ¡ERES EL IMPOSTOR!
                </h2>
                <p className="text-gray-400 text-lg">
                  No conoces la palabra secreta. Intenta pasar desapercibido dando pistas creíbles.
                </p>
                <div className="bg-red-900/30 p-4 rounded-lg">
                  <p className="text-red-300 text-sm">
                    Escucha las pistas de los demás e intenta adivinar la palabra para sobrevivir.
                  </p>
                </div>
              </>
            ) : (
              // Es civil
              <>
                <div className="text-6xl">✅</div>
                <h2 className="text-xl font-bold text-green-500">
                  La palabra secreta es:
                </h2>
                <p className="text-5xl font-bold text-gold py-4">
                  {secretWord}
                </p>
                <p className="text-gray-400">
                  Da pistas de UNA palabra que ayuden a identificar al impostor sin revelar la palabra.
                </p>
              </>
            )}
            
            <Button 
              className="w-full btn-primary-mk text-lg py-6"
              onClick={nextPlayerDistribution}
            >
              <EyeOff className="mr-2" /> Entendido, siguiente
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Renderizar fase de juego (dar pistas)
  const renderPlaying = () => (
    <div className="max-w-lg mx-auto space-y-6">
      <Card className="card-mk">
        <CardHeader className="text-center">
          <div className="text-sm text-gray-400 mb-2">Ronda {currentRound} de {numRounds}</div>
          <CardTitle className="text-gold text-2xl">🎯 Ronda de Pistas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <p className="text-gray-400 mb-2">Por turnos, cada jugador dice UNA palabra relacionada</p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {playerNames.map((name, idx) => (
                <span 
                  key={idx} 
                  className={`px-3 py-1 rounded-full text-sm ${
                    idx === impostorIndex 
                      ? 'bg-gray-700 text-gray-300' 
                      : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  {idx + 1}. {name}
                </span>
              ))}
            </div>
          </div>
          
          <div className="bg-yellow-900/30 border border-yellow-600/50 p-4 rounded-lg">
            <h3 className="text-yellow-400 font-bold mb-2">📋 Reglas:</h3>
            <ul className="text-yellow-200 text-sm space-y-1">
              <li>• Una sola palabra por turno</li>
              <li>• No repetir palabras de otros</li>
              <li>• No decir la palabra exacta</li>
              <li>• Sin gestos ni explicaciones</li>
            </ul>
          </div>

          <Button 
            className="w-full btn-primary-mk text-lg py-6"
            onClick={goToVoting}
          >
            <Vote className="mr-2" /> Pasar a Votación
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // Renderizar fase de votación
  const renderVoting = () => (
    <div className="max-w-md mx-auto space-y-6">
      <Card className="card-mk overflow-hidden">
        <CardContent className="p-0">
          <div className="bg-gradient-to-b from-purple-600 to-purple-700 p-6 text-center">
            <img 
              src={IMPOSTOR_IMAGE} 
              alt="Votación" 
              className="w-32 h-32 mx-auto rounded-full border-4 border-white shadow-lg"
            />
          </div>
          <div className="p-6 space-y-4">
            <h2 className="text-xl font-bold text-gold text-center">
              🗳️ Turno de votar
            </h2>
            <p className="text-3xl font-bold text-white text-center">
              {playerNames[currentVoterIndex]}
            </p>
            <p className="text-gray-400 text-center text-sm">
              ¿Quién crees que es el impostor?
            </p>
            
            <div className="grid gap-2 mt-4">
              {playerNames.map((name, idx) => (
                idx !== currentVoterIndex && (
                  <Button
                    key={idx}
                    variant="outline"
                    className="w-full border-gray-600 hover:border-gold hover:bg-gold/10 py-4"
                    onClick={() => castVote(idx)}
                  >
                    <Target className="mr-2 w-4 h-4" />
                    {name}
                  </Button>
                )
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Renderizar resultados de la ronda
  const renderResults = () => {
    const lastResult = roundResults[roundResults.length - 1];
    
    return (
      <div className="max-w-md mx-auto space-y-6">
        <Card className={`card-mk border-2 ${lastResult.caught ? 'border-green-500' : 'border-red-500'}`}>
          <CardContent className="p-8 text-center space-y-6">
            <div className="text-6xl">
              {lastResult.caught ? '🎉' : '😈'}
            </div>
            
            <h2 className={`text-3xl font-bold ${lastResult.caught ? 'text-green-500' : 'text-red-500'}`}>
              {lastResult.caught ? '¡Impostor Descubierto!' : '¡El Impostor Gana!'}
            </h2>
            
            <div className="space-y-3">
              <p className="text-gray-400">La palabra era:</p>
              <p className="text-3xl font-bold text-gold">{lastResult.word}</p>
            </div>
            
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-400 mb-2">El impostor era:</p>
              <p className="text-2xl font-bold text-red-400">{lastResult.impostor}</p>
            </div>
            
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-400 mb-2">Más votado:</p>
              <p className="text-xl font-bold text-white">{lastResult.mostVoted}</p>
              <p className="text-sm text-gray-500">({lastResult.votes[lastResult.mostVoted]} votos)</p>
            </div>

            {/* Puntuaciones actuales */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gold font-bold mb-2">Puntuaciones:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {Object.entries(scores)
                  .sort((a, b) => b[1] - a[1])
                  .map(([name, score]) => (
                    <span key={name} className="px-3 py-1 bg-gray-700 rounded-full text-sm">
                      {name}: <span className="text-gold font-bold">{score}</span>
                    </span>
                  ))}
              </div>
            </div>
            
            <Button 
              className="w-full btn-primary-mk text-lg py-6"
              onClick={nextRoundOrEnd}
            >
              {currentRound < numRounds ? (
                <>
                  <ChevronRight className="mr-2" /> Siguiente Ronda
                </>
              ) : (
                <>
                  <Trophy className="mr-2" /> Ver Resultados Finales
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Renderizar resultados finales
  const renderFinal = () => {
    // Ordenar por puntuación
    const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const winner = sortedScores[0];
    
    return (
      <div className="max-w-md mx-auto space-y-6">
        <Card className="card-mk border-2 border-gold">
          <CardContent className="p-8 text-center space-y-6">
            <div className="text-6xl">🏆</div>
            
            <h2 className="text-3xl font-bold text-gold">
              ¡Fin de la Partida!
            </h2>
            
            <div className="bg-gradient-to-b from-yellow-600 to-yellow-700 p-6 rounded-lg">
              <p className="text-yellow-200 mb-2">Ganador/a:</p>
              <p className="text-4xl font-bold text-white">{winner[0]}</p>
              <p className="text-2xl text-yellow-200">{winner[1]} puntos</p>
            </div>
            
            {/* Tabla de puntuaciones */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gold font-bold mb-3">Clasificación Final:</p>
              <div className="space-y-2">
                {sortedScores.map(([name, score], idx) => (
                  <div 
                    key={name} 
                    className={`flex justify-between items-center p-2 rounded ${
                      idx === 0 ? 'bg-yellow-900/50' : 'bg-gray-700/50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-gray-400">{idx + 1}.</span>
                      {idx === 0 && <span>👑</span>}
                      <span className={idx === 0 ? 'text-gold font-bold' : 'text-white'}>{name}</span>
                    </span>
                    <span className="text-gold font-bold">{score} pts</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Resumen de rondas */}
            <div className="bg-gray-800 p-4 rounded-lg text-left">
              <p className="text-gold font-bold mb-2">Resumen:</p>
              {roundResults.map((result, idx) => (
                <div key={idx} className="text-sm py-1 border-b border-gray-700 last:border-0">
                  <span className="text-gray-400">R{result.round}:</span>{' '}
                  <span className="text-white">{result.word}</span>{' - '}
                  <span className={result.caught ? 'text-green-400' : 'text-red-400'}>
                    {result.caught ? `${result.impostor} descubierto` : `${result.impostor} escapó`}
                  </span>
                </div>
              ))}
            </div>
            
            <Button 
              className="w-full btn-primary-mk text-lg py-6"
              onClick={resetGame}
            >
              <RotateCcw className="mr-2" /> Nueva Partida
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-gold"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-gold">🎭 El Impostor</h1>
            <p className="text-gray-400">
              {gamePhase === 'setup' && 'Configura tu partida'}
              {gamePhase === 'distribution' && `Ronda ${currentRound} - Reparto de roles`}
              {gamePhase === 'playing' && `Ronda ${currentRound} - Turno de pistas`}
              {gamePhase === 'voting' && `Ronda ${currentRound} - Votación`}
              {gamePhase === 'results' && `Ronda ${currentRound} - Resultados`}
              {gamePhase === 'final' && 'Resultados finales'}
            </p>
          </div>
        </div>

        {/* Contenido según la fase */}
        {gamePhase === 'setup' && renderSetup()}
        {gamePhase === 'distribution' && renderDistribution()}
        {gamePhase === 'playing' && renderPlaying()}
        {gamePhase === 'voting' && renderVoting()}
        {gamePhase === 'results' && renderResults()}
        {gamePhase === 'final' && renderFinal()}
      </div>
    </div>
  );
}

export default Impostor;
